import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Button, buttonClasses,
  Card,
  EmptyState,
  IconChevronRight,
  MicroLabel,
  SkillRadar,
  type SkillRadarEntry,
} from "@bht/ui";
import { useViewport } from "../lib/viewport.js";
import { loadSession } from "../lib/session.js";
import {
  fetchMastery,
  fetchReviewDue,
  recentAttempts,
  RECENT_ATTEMPTS_MAX,
  RECENT_WINDOW_DAYS,
  fetchMe,
  fetchScoreHistory,
  fetchMissReasons,
  recordDailyGoal,
  type MissReason,
  type MasteryRow,
  type ScoreHistoryPoint,
  type ExamTarget,
} from "../lib/api.js";
import { targetModuleSections, defaultModuleForTargets } from "../lib/modules.js";
import { computePlan, activeStreak, projectTrajectory, PHASE_LABEL, type Trajectory } from "../lib/study-plan.js";
import { friendlyError } from "../lib/errors.js";
import { reportFunnel } from "../lib/report-error.js";
import { FeedbackReply } from "../components/FeedbackReply.js";
// Auto-generated from data/eval-set/*/approved.json at build time (see
// scripts/gen-corpus-counts.mjs) so these never go stale on a corpus promote.
import { CORPUS_COUNTS as MODULE_ITEM_COUNTS } from "../lib/corpus-counts.js";

type RecentAttempt = {
  id: string;
  module: string;
  skill_family: string;
  snap_latency_ms?: number | null;
  is_correct: number;
  attempted_at: number;
};
import { moduleBlurb, moduleLabel, skillFamilyShort } from "../lib/labels.js";
import { AppShell } from "../components/AppShell.js";
import { RepairCard } from "../components/RepairCard.js";
import { SkillSummary } from "../components/SkillSummary.js";
import { HiddenTestCard } from "../components/HiddenTestCard.js";
import { PracticeTestCard } from "../components/PracticeTestCard.js";

function fmtRelTime(epochMs: number): string {
  const delta = Date.now() - epochMs;
  if (delta < 60_000) return "moments ago";
  if (delta < 60 * 60_000) return `${Math.floor(delta / 60_000)} minutes ago`;
  if (delta < 24 * 60 * 60_000) return `${Math.floor(delta / (60 * 60_000))} hours ago`;
  return `${Math.floor(delta / (24 * 60 * 60_000))} days ago`;
}

/**
 * How far back the median reaction figure reaches, in days.
 *
 * Deliberately tighter than the load window, and named on the stat itself, so
 * the number means something a learner can act on. A median over three months
 * is not a reading of how they are answering now.
 */
const MEDIAN_WINDOW_DAYS = 30;

export function Home() {
  const session = loadSession();

  const [mastery, setMastery] = useState<MasteryRow[] | null>(null);
  const [recent, setRecent] = useState<RecentAttempt[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [reviewDue, setReviewDue] = useState<{
    total: number;
    by_module: Record<string, number>;
  } | null>(null);
  const [targetDate, setTargetDate] = useState<string | null>(null);
  const [targetBand, setTargetBand] = useState<string | null>(null);
  const [targetExams, setTargetExams] = useState<ExamTarget[]>([]);
  const targetsAct = targetExams.includes("ACT");
  const targetsSat = targetExams.includes("SAT");
  // SAT/ACT-specific practice tools only earn a spot on Home for those targets.
  // An empty target list (still loading, or a legacy account) defaults to showing
  // them, since SAT is the default target and the common case.
  const showSatActTools = targetExams.length === 0 || targetsSat || targetsAct;
  // A few surfaces speak digital-SAT mechanics (adaptive Module 1/2, the SAT
  // practice test) and only make sense for an SAT target (or unknown/default).
  const showSat = targetExams.length === 0 || targetsSat;
  const [scoreHistory, setScoreHistory] = useState<ScoreHistoryPoint[]>([]);
  // Tagged miss reasons drive WHAT KIND of work the plan prescribes. Optional:
  // the plan falls back to weakest-module targeting when too few are tagged.
  const [missReasons, setMissReasons] = useState<Record<MissReason, number> | null>(null);
  const [persistedStreak, setPersistedStreak] = useState<number | null>(null);

  // Funnel: reached home (the end of the onboarding path).
  useEffect(() => {
    reportFunnel("reached_home");
  }, []);

  useEffect(() => {
    let cancelled = false;
    Promise.all([fetchMastery(), recentAttempts(RECENT_ATTEMPTS_MAX, RECENT_WINDOW_DAYS)])
      .then(([m, r]) => {
        if (!cancelled) {
          setMastery(m.rows);
          setRecent(r.attempts as RecentAttempt[]);
        }
      })
      .catch((e) => {
        if (!cancelled) setError(friendlyError(e));
      });
    // Spaced-rep due counts, fetched independently so a failure never blanks Home.
    fetchReviewDue()
      .then((d) => {
        if (!cancelled) setReviewDue(d);
      })
      .catch(() => undefined);
    // Target date drives the study-plan phasing; independent so a failure never blanks Home.
    fetchMe()
      .then((me) => {
        if (!cancelled) {
          setTargetDate(me.target_date);
          setTargetBand(me.target_band);
          setTargetExams(me.target_exams);
        }
      })
      .catch(() => undefined);
    // Completed-test history feeds the trajectory projection (B4).
    fetchScoreHistory()
      .then((h) => {
        if (!cancelled) setScoreHistory(h.tests);
      })
      .catch(() => undefined);
    // Tagged miss reasons: what kind of work the plan should prescribe. Optional
    // by design, so a failure leaves the previous weakest-module behavior.
    fetchMissReasons()
      .then((r) => {
        if (!cancelled) setMissReasons(r.counts);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, []);

  // The module with the most items due for review (drives the review CTA target).
  const dueModule = reviewDue
    ? Object.entries(reviewDue.by_module).sort((a, b) => b[1] - a[1])[0]?.[0]
    : undefined;

  const radarEntries: SkillRadarEntry[] = (mastery ?? []).map((m) => ({
    skillFamily: skillFamilyShort(m.skill_family),
    mastery: m.attempts > 0 ? m.correct / m.attempts : 0,
    medianSnapMs: m.mean_snap_ms ?? undefined,
  }));

  const totalAttempts = (mastery ?? []).reduce((a, m) => a + m.attempts, 0);
  const totalCorrect = (mastery ?? []).reduce((a, m) => a + m.correct, 0);
  const accuracy =
    totalAttempts > 0 ? Math.round((totalCorrect / totalAttempts) * 100) : null;

  const weakest =
    (mastery ?? [])
      .filter((m) => m.attempts >= 1)
      .sort((a, b) => a.correct / a.attempts - b.correct / b.attempts)[0] ?? null;

  const primaryModule = weakest?.module ?? defaultModuleForTargets(targetExams);
  const primaryModuleLabel = moduleLabel(primaryModule);

  const lastAttempted = Math.max(0, ...((mastery ?? []).map((m) => m.last_attempted)));

  /**
   * Median reaction time over the last MEDIAN_WINDOW_DAYS days.
   *
   * This used to run over whatever `recentAttempts(300)` returned, which was a
   * ROW count and not a lookback, so 25 answers from 85 days ago were averaged
   * and shown under a label a learner reads as "lately". Worse, the server caps
   * that endpoint at 200, so the 300 was never honoured either.
   *
   * Now it is a real window, and it returns null when the window is empty rather
   * than reaching further back for something to display. A missing number is
   * honest; a stale one is not.
   */
  const medianReactionMs = useMemo(() => {
    const cutoff = Date.now() - MEDIAN_WINDOW_DAYS * 24 * 60 * 60_000;
    const ms = recent
      .filter((a) => new Date(a.attempted_at).getTime() >= cutoff)
      .map((a) => a.snap_latency_ms)
      .filter((x): x is number => typeof x === "number" && x > 0)
      .sort((a, b) => a - b);
    if (ms.length === 0) return null;
    return ms[Math.floor(ms.length / 2)];
  }, [recent]);

  /** Attempts per day for the last 7 days (today first). */
  const dayActivity = useMemo(() => {
    const days: Array<{ date: Date; label: string; mm: string; count: number }> = [];
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const DAY = 24 * 60 * 60_000;
    for (let i = 6; i >= 0; i--) {
      const d = new Date(today.getTime() - i * DAY);
      const label = (["S", "M", "T", "W", "T", "F", "S"][d.getDay()] ?? "?");
      const mm = String(d.getDate()).padStart(2, "0");
      const count = recent.filter((a) => {
        const ad = new Date(a.attempted_at);
        ad.setHours(0, 0, 0, 0);
        return ad.getTime() === d.getTime();
      }).length;
      days.push({ date: d, label, mm, count });
    }
    return days;
  }, [recent]);

  const isDesktop = useViewport() === "desktop";
  const showRail = isDesktop && totalAttempts > 0;

  // Status block — your attempts / accuracy / streak / Hidden Test score. On desktop
  // it moves into the 380px right rail (fills the width and separates "where I am"
  // from "what to do next"); on mobile it renders inline at the top of the main column.
  const statusRail = (
    <div className="app-stack" style={{ gap: "var(--sp-4)" }}>
      {totalAttempts > 0 && (
        <div className="app-stack" style={{ gap: "var(--sp-3)" }}>
          <div
            style={{ display: "flex", gap: "var(--sp-4)", alignItems: "baseline", flexWrap: "wrap" }}
          >
            <HeroStat value={String(totalAttempts).padStart(2, "0")} label="Attempts" color="var(--accent-amber)" size={36} />
            {accuracy != null && <HeroStat value={`${accuracy}%`} label="Accuracy" color="var(--fg-0)" size={28} />}
            {medianReactionMs != null && (
              <HeroStat value={`${(medianReactionMs / 1000).toFixed(1)}s`} label={`Reaction · median · ${MEDIAN_WINDOW_DAYS}d`} color="var(--fg-0)" size={28} />
            )}
          </div>
          {lastAttempted > 0 && <MicroLabel>Last practiced {fmtRelTime(lastAttempted)}</MicroLabel>}
        </div>
      )}
      {totalAttempts > 0 && (
        <div>
          <MicroLabel className="app-stack-block-bottom">Last 7 days</MicroLabel>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 6, marginTop: 8 }}>
            {dayActivity.map((d, i) => (
              <DayCell key={i} label={d.label} mm={d.mm} count={d.count} isToday={i === dayActivity.length - 1} />
            ))}
          </div>
        </div>
      )}
      <HiddenTestCard />
    </div>
  );

  // Secondary practice modes — a compact list (was four tall cards whose ghost
  // full-width buttons read as floating centered text).
  const quickActions = [
    weakest && totalAttempts >= 8 && weakest.attempts >= 3 && weakest.correct < weakest.attempts
      ? { key: "weak", label: "Drill weak spots", to: `/app/drill/${weakest.module}?mode=weakness`, sub: `Your weak spot in ${moduleLabel(weakest.module)}: ${skillFamilyShort(weakest.skill_family).toLowerCase()}. Includes ones you recently missed.` }
      : null,
    totalAttempts < 5
      ? { key: "diag", label: "Take the diagnostic", to: "/app/diagnostic", sub: "A 15-question check across every skill maps where you stand." }
      : null,
    reviewDue && reviewDue.total > 0 && dueModule
      ? { key: "review", label: `Review ${reviewDue.total} due`, to: `/app/drill/${dueModule}?mode=review`, sub: `${reviewDue.total} item${reviewDue.total > 1 ? "s" : ""} due for spaced review, most in ${moduleLabel(dueModule)}.` }
      : null,
    totalAttempts >= 8 && showSatActTools
      ? { key: "speed", label: "Speed round", to: "/app/drill/grammar?mode=speed", sub: `Beat the clock: timed ${moduleLabel("grammar")}, about 12 seconds a question.` }
      : null,
    // Hard sets were reachable only as a toggle on the practice-test page, so a
    // student had to already know they existed. Production bears that out: the
    // gauntlet, which has a card here, has been started twice as often as the hard
    // set, which did not. A learner asked outright where to find them.
    totalAttempts >= 8 && showSat
      ? { key: "hard-set", label: "Hard practice test", to: "/app/practice-test?hard=1", sub: "A full test built only from questions measured harder than the hardest official ones. Scored as a raw count, because the mix is deliberately not the real test's." }
      : null,
    totalAttempts >= 8 && showSat
      ? { key: "gauntlet", label: "Hard Math gauntlet", to: "/app/practice-test", sub: "22 of the hardest Math questions in 35 minutes, on the real clock. Trains the late-module crunch." }
      : null,
    totalAttempts >= 8 && showSat
      ? { key: "m1", label: "SAT Module 1 practice", to: "/app/drill/slice?mode=m1", sub: `10 ${moduleLabel("slice")} questions that show, live, whether you are on track to reach the harder Module 2. Get 7 right to reach it. Builds early-test accuracy under pressure.` }
      : null,
    showSatActTools
      ? { key: "graph", label: "Graph Gym", to: "/app/graph-gym", sub: "Read slope, intercepts, a vertex, or zeros straight off the plot. Quick reps on the graph skills the Math section leans on." }
      : null,
    totalAttempts >= 8 && showSatActTools
      ? { key: "pace", label: "Pace trainer", to: "/app/pace", sub: "Running out of time? Drill the per-question rhythm for any SAT or ACT section and see whether your pace finishes it." }
      : null,
    totalAttempts >= 8 && showSatActTools
      ? { key: "mental-math", label: "Mental math", to: "/app/mental-math", sub: "Fast reps on the facts worth knowing cold: conversions, squares, cubes, roots, and Pythagorean triples." }
      : null,
    targetsAct
      ? { key: "trig-values", label: "Unit circle", to: "/app/trig-values", sub: "Know sin, cos, and tan of the common angles cold. Quick reps on the exact values the ACT leans on." }
      : null,
    targetsAct
      ? { key: "act-stems", label: "ACT English stems", to: "/app/act-stems", sub: "On the ACT, every English question now spells out its task. Practice reading the stem and knowing the move it asks for." }
      : null,
    targetsAct
      ? { key: "act-formulas", label: "ACT Math formulas", to: "/app/act-formulas", sub: "The ACT gives no formula sheet, so you have to know them cold. Quick reps on the formulas the test expects you to have memorized." }
      : null,
    targetsAct
      ? { key: "act-science", label: "Should you take ACT Science?", to: "/app/act-science", sub: "The Science section is optional now. Answer a few questions about your colleges and yourself for an honest read on whether you need it." }
      : null,
    totalAttempts < 5 && showSatActTools
      ? { key: "choice", label: "SAT or ACT?", to: "/app/test-choice", sub: "Five quick questions plus your own results, weighed into a clear, honest recommendation of which test fits you." }
      : null,
  ].filter((a): a is { key: string; label: string; to: string; sub: string } => a != null);

  // Study plan (#2): ordered "what to do today", phased by weeks-to-test.
  const plan = useMemo(
    () =>
      computePlan({
        targetDate,
        today: new Date(),
        hasDiagnostic: totalAttempts >= 5,
        reviewDueTotal: reviewDue?.total ?? 0,
        reviewDueModule: dueModule,
        weakestModule: weakest?.module,
        weakestSkillLabel: weakest ? skillFamilyShort(weakest.skill_family).toLowerCase() : undefined,
        defaultModule: defaultModuleForTargets(targetExams),
        missReasons: missReasons ?? undefined,
      }),
    // `today` is stable within a day and intentionally omitted from deps.
    [targetDate, totalAttempts, reviewDue, dueModule, weakest, targetExams, missReasons],
  );
  const streak = useMemo(
    () => activeStreak(recent.map((a) => a.attempted_at), new Date()),
    [recent],
  );
  // Ahead / on-track / behind vs the dated goal (B4). Null until the user has a
  // goal band + date and >=2 completed standard practice tests.
  const trajectory: Trajectory | null = useMemo(
    () => projectTrajectory(scoreHistory, targetBand, targetDate, new Date()),
    [scoreHistory, targetBand, targetDate],
  );
  const todayCount = dayActivity[dayActivity.length - 1]?.count ?? 0;

  // Persist today's goal progress and read back the durable goal-met streak.
  // Falls back to the stateless active-day streak if the write fails (offline).
  useEffect(() => {
    if (totalAttempts === 0) return;
    const n = new Date();
    const day = `${n.getFullYear()}-${String(n.getMonth() + 1).padStart(2, "0")}-${String(n.getDate()).padStart(2, "0")}`;
    recordDailyGoal({ day, goal: plan.dailyGoalItems, done: todayCount })
      .then((r) => setPersistedStreak(r.streak))
      .catch(() => undefined);
  }, [totalAttempts, plan.dailyGoalItems, todayCount]);
  const shownStreak = persistedStreak ?? streak;

  // Primary CTA. Hoisted so mobile can render it ABOVE the status block:
  // both 2026-07-28 design reviews found that a student opening the app on a
  // phone met attempts, accuracy, a 7-day grid and the Hidden Test card before
  // the one action that answers "what do I do now?". On desktop the status
  // block lives in the right rail, so ordering is only a mobile problem.
  /**
   * A brand-new account gets a CHOICE, not a recommendation.
   *
   * Measured 2026-09-16: 71% of students who ever start a full practice test
   * make it their first action, 34% of them answer nothing at all, and only 39%
   * reach question five — but three quarters of those who pass question five
   * finish the whole 2h14. The loss is in the first minute, not in the length.
   *
   * So the fix is not to hide the full test, which a real population arrives
   * specifically wanting. It is to put a six-minute option beside it at
   * comparable weight. Both are labelled by what the student GETS, because "Start"
   * against "Start" is not a choice, it is two buttons.
   *
   * Deliberately absent: any framing that makes the short one the lesser option
   * ("not ready for a full test?"), and any nudge that diverts someone who came
   * with a purpose ("we recommend starting here").
   */
  useEffect(() => {
    // Impression, not render: without a denominator "nobody clicks the
    // diagnostic" and "nobody is shown the diagnostic" look identical, which is
    // how it reached four users in the first place.
    if (totalAttempts === 0) reportFunnel("diagnostic_offered");
  }, [totalAttempts]);

  const coldStartChoice = (
    <div className="app-stack" style={{ gap: "var(--sp-3)" }}>
      <Card armed>
        <div className="app-stack">
          <MicroLabel color="accent">New here?</MicroLabel>
          <h2
            style={{
              color: "var(--fg-0)",
              fontSize: "var(--t-h1)",
              fontWeight: 600,
              lineHeight: 1.3,
              margin: 0,
            }}
          >
            Find your starting point
          </h2>
          <p style={{ color: "var(--fg-2)", fontSize: "var(--t-body)", lineHeight: 1.55 }}>
            15 questions, about 6 minutes. See what to practice first.
          </p>
          <Link
            to="/app/diagnostic"
            onClick={() => reportFunnel("diagnostic_offered_click")}
            style={{ alignSelf: "stretch" }}
            className={buttonClasses({ variant: "primary", full: true })}
          >
            Start quick diagnostic
          </Link>
        </div>
      </Card>
      <Card>
        <div className="app-stack">
          <MicroLabel>Ready for the full test?</MicroLabel>
          <p style={{ color: "var(--fg-2)", fontSize: "var(--t-body)", lineHeight: 1.55, margin: 0 }}>
            A complete digital SAT practice test, scored 400 to 1600.
            {/* The duration goes ON the control. Offering a 2h14 commitment with
                no time on the button, right beside a six-minute one, is how a
                student ends up in something they did not agree to. */}
          </p>
          <Link
            to="/app/practice-test"
            onClick={() => reportFunnel("diagnostic_full_test_offered_click")}
            style={{ alignSelf: "stretch" }}
            className={buttonClasses({ variant: "secondary", full: true })}
          >
            Take a full practice test · 2 hr 14 min
          </Link>
        </div>
      </Card>
    </div>
  );

  const primaryCta = (
    <Card armed>
      <div className="app-stack">
        <MicroLabel color="accent">Recommended for you</MicroLabel>
        <h2
          style={{
            color: "var(--fg-0)",
            fontSize: "var(--t-h1)",
            fontWeight: 600,
            lineHeight: 1.3,
            margin: 0,
          }}
        >
          {totalAttempts === 0
            ? `5 ${primaryModuleLabel} questions`
            : weakest
            ? `5 more ${primaryModuleLabel} questions`
            : `5 more ${primaryModuleLabel} questions`}
        </h2>
        <p style={{ color: "var(--fg-2)", fontSize: "var(--t-body)", lineHeight: 1.55 }}>
          {totalAttempts === 0
            ? "About 3 minutes. We'll start finding the kinds of questions that cost you points."
            : weakest
            ? `Weighted toward your weakest skill: ${skillFamilyShort(weakest.skill_family).toLowerCase()}. We'll skip questions you've seen this week.`
            : "Fresh questions. None repeated from your last week of practice."}
        </p>
        <Link to={`/app/drill/${primaryModule}`} style={{ alignSelf: "stretch" }} className={buttonClasses({ variant: "primary", full: true })}>{totalAttempts === 0 ? "Start" : "Continue"}</Link>
      </div>
    </Card>
  );

  return (
    <AppShell rightRail={showRail ? statusRail : undefined}>
      <div
        className="app-stack"
        style={{
          gap: "var(--sp-5)",
          // On desktop with the rail, fill the main cell (capped so cards don't
          // stretch too wide) instead of a narrow 760px column floating with a gap.
          maxWidth: showRail ? 1040 : 880,
          margin: showRail ? 0 : "0 auto",
          padding: "24px 20px 32px",
        }}
      >
        {/* Renders nothing unless we have answered something this learner sent. */}
        <FeedbackReply />

        {totalAttempts === 0 && (
          <h1
            style={{
              color: "var(--fg-0)",
              fontFamily: "var(--font-sans)",
              fontSize: "var(--t-display-2)",
              fontWeight: 600,
              lineHeight: 1.15,
              letterSpacing: "-0.01em",
              margin: 0,
            }}
          >
            Find what's costing you points.
          </h1>
        )}

        {/* Unfinished work from the last practice test, ABOVE the generic
            "start practising" call to action. A student who left a repair set
            half done has already told us what they wanted to do next, and the
            plan's whole argument is that Home should hold the next unfinished
            commitment rather than offer a fresh open-ended choice. Renders
            nothing when there is no open set, which is the common case. */}
        <RepairCard source={{ kind: "open" }} funnelStep="repair_card_seen_home" />

        {/* Mobile: the action comes before the history. */}
        {!showRail && (totalAttempts === 0 ? coldStartChoice : primaryCta)}
        {!showRail && statusRail}

        {/* Study plan — ordered "what to do today", phased by weeks-to-test (#2). */}
        {totalAttempts > 0 && plan.steps.length > 0 && (
          <Card>
            <div className="app-stack" style={{ gap: "var(--sp-3)" }}>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "baseline",
                  gap: "var(--sp-2)",
                  flexWrap: "wrap",
                }}
              >
                <MicroLabel color="accent">Today · {PHASE_LABEL[plan.phase]}</MicroLabel>
                <span style={{ fontSize: "var(--t-small)", color: "var(--fg-2)" }}>
                  {plan.weeksToTest != null
                    ? `${plan.weeksToTest} wk${plan.weeksToTest === 1 ? "" : "s"} to test · `
                    : ""}
                  {shownStreak > 0 ? `${shownStreak}-day streak` : "start a streak"}
                </span>
                {/* Caption-sized type, but the tap area still has to clear 44px
                    (CLAUDE.md, enforced by e2e/tap-targets.spec.ts). Padding does
                    it without enlarging the text or the row's visual weight. */}
                <Link
                  to="/app/plan"
                  style={{
                    fontSize: "var(--t-small)",
                    color: "var(--fg-2)",
                    display: "inline-flex",
                    alignItems: "center",
                    minHeight: 44,
                    paddingBlock: 10,
                  }}
                >
                  {plan.weeksToTest != null ? "See the whole plan" : "How long do you have?"}
                </Link>
              </div>
              <div style={{ fontSize: "var(--t-small)", color: "var(--fg-2)" }}>
                {todayCount} / {plan.dailyGoalItems} questions today
              </div>
              {trajectory && (
                <div
                  style={{
                    fontSize: "var(--t-small)",
                    color:
                      trajectory.status === "behind"
                        ? "var(--accent-amber)"
                        : trajectory.status === "ahead"
                        ? "var(--signal-green)"
                        : "var(--fg-2)",
                  }}
                >
                  {trajectory.status === "ahead"
                    ? `Ahead of your goal: trending toward ~${trajectory.projected} vs ${trajectory.goal}+.`
                    : trajectory.status === "on_track"
                    ? `On track: trending toward ~${trajectory.projected} for your ${trajectory.goal}+ goal.`
                    : `Behind your ${trajectory.goal}+ goal: trending toward ~${trajectory.projected}. The plan below closes the gap.`}
                </div>
              )}
              <div className="app-stack" style={{ gap: "var(--sp-2)" }}>
                {plan.steps.map((s, i) => (
                  <Link
                    key={s.key}
                    to={s.to}
                    style={{
                      display: "block",
                      padding: "10px 12px",
                      borderRadius: "var(--r-2)",
                      background: "var(--surface-2, rgba(255,255,255,0.03))",
                      border: "1px solid var(--border)",
                    }}
                  >
                    <div style={{ color: "var(--fg-0)", fontWeight: 600, fontSize: "var(--t-body)" }}>
                      {i + 1}. {s.label}
                    </div>
                    <div style={{ color: "var(--fg-2)", fontSize: "var(--t-small)", marginTop: 2 }}>
                      {s.sub}
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </Card>
        )}

        {showRail && (totalAttempts === 0 ? coldStartChoice : primaryCta)}

        {/* Quick actions — secondary practice modes as a compact list (icon-less
            rows with a chevron), instead of four tall ghost-button cards. */}
        {quickActions.length > 0 && (
          <Card tight>
            <MicroLabel className="app-stack-block-bottom">Quick actions</MicroLabel>
            <div className="app-stack" style={{ gap: 0 }}>
              {quickActions.map((a) => (
                <QuickAction key={a.key} label={a.label} sub={a.sub} to={a.to} />
              ))}
            </div>
          </Card>
        )}

        {/* Module list */}
        <div>
          <h3
            style={{
              color: "var(--fg-0)",
              fontSize: "var(--t-h3)",
              fontWeight: 600,
              margin: 0,
              marginBottom: "var(--sp-3)",
            }}
          >
            Choose a subject
          </h3>
          {(() => {
            const sections = targetModuleSections(targetExams);
            const showHeads = sections.length > 1;
            return (
              <div className="app-stack" style={{ gap: "var(--sp-4)" }}>
                {sections.map((section) => (
                  <div key={section.key}>
                    {showHeads && (
                      <MicroLabel as="h4" className="app-stack-block-bottom">{section.label}</MicroLabel>
                    )}
                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns: "1fr 1fr",
                        gap: 8,
                        marginTop: showHeads ? 6 : 0,
                      }}
                    >
                      {section.modules.map((id) => {
                        const moduleMastery = (mastery ?? []).filter((r) => r.module === id);
                        const moduleAttempts = moduleMastery.reduce((a, r) => a + r.attempts, 0);
                        const moduleCorrect = moduleMastery.reduce((a, r) => a + r.correct, 0);
                        const moduleAccuracy =
                          moduleAttempts > 0 ? moduleCorrect / moduleAttempts : null;
                        return (
                          <ModuleTile
                            key={id}
                            id={id}
                            accuracy={moduleAccuracy}
                            attempts={moduleAttempts}
                            correct={moduleCorrect}
                            itemCount={MODULE_ITEM_COUNTS[id]}
                          />
                        );
                      })}
                    </div>
                  </div>
                ))}
                <Link
                  to="/app/packs"
                  className="bht-tap-44-inline"
                  style={{
                    fontFamily: "var(--font-mono)",
                    fontSize: 11,
                    color: "var(--fg-2)",
                    letterSpacing: "0.04em",
                    textTransform: "uppercase",
                  }}
                >
                  Browse all subjects and tools →
                </Link>
              </div>
            );
          })()}
        </div>

        {/* Skill map */}
        {totalAttempts > 0 ? (
          <Card>
            <div className="app-stack">
              <h3
                style={{
                  color: "var(--fg-0)",
                  fontSize: "var(--t-h3)",
                  fontWeight: 600,
                  margin: 0,
                }}
              >
                Your skill map
              </h3>
              {error && <p style={{ color: "var(--signal-red)" }}>{error}</p>}
              {mastery == null && !error && <p style={{ color: "var(--fg-3)" }}>Loading…</p>}
              {mastery != null && radarEntries.length >= 3 && (
                <>
                  <SkillRadar entries={radarEntries} size={240} />
                  <SkillSummary mastery={mastery} />
                </>
              )}
              {mastery != null && radarEntries.length < 3 && (
                <p style={{ color: "var(--fg-2)", fontSize: "var(--t-small)" }}>
                  Practice a few more questions to see the full breakdown.
                </p>
              )}
              <Link
                to="/app/mistakes"
                // Same reason as the quick actions: the redesign wants to move
                // the skill map off Home and we have no evidence anyone opens it.
                onClick={() => reportFunnel("home_skillmap_cta")}
                style={{ alignSelf: "stretch" }}
              >
                <Button variant="ghost" full>
                  See what's costing you points
                </Button>
              </Link>
            </div>
          </Card>
        ) : (
          <Card>
            <EmptyState message="As you practice, we'll show you exactly which kinds of questions cost you the most points." />
          </Card>
        )}

        {/* Quiet bottom entry: full-length practice test (digital-SAT format, so
            only for an SAT target or the unknown/default case). */}
        {showSat && <PracticeTestCard />}
      </div>
    </AppShell>
  );
}

export function QuickAction({
  label,
  sub,
  to,
  // Optional so the redesigned home can keep its taps separable from this
  // screen's. Defaults to the existing event, so current Home is unchanged.
  event,
}: {
  label: string;
  sub: string;
  to: string;
  event?: string;
}) {
  return (
    <Link
      to={to}
      // Which home entry points are actually used is unknown: the only funnel
      // events that exist are reached_home and the two onboarding steps. A
      // redesign wants to cut things from this screen, and cutting without this
      // is deleting on opinion.
      onClick={() => reportFunnel(event ?? `home_quick:${to}`)}
      className="bht-quick-action"
      style={{
        display: "flex",
        alignItems: "center",
        gap: 12,
        padding: "11px 4px",
        borderTop: "1px solid var(--border-faint)",
        textDecoration: "none",
      }}
    >
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ color: "var(--fg-0)", fontSize: "var(--t-body)", fontWeight: 500 }}>{label}</div>
        <div style={{ color: "var(--fg-2)", fontSize: "var(--t-small)", marginTop: 2, lineHeight: 1.4 }}>{sub}</div>
      </div>
      <IconChevronRight size={16} color="var(--fg-3)" />
    </Link>
  );
}

function HeroStat({
  value,
  label,
  color,
  size,
}: {
  value: string;
  label: string;
  color: string;
  size: number;
}) {
  return (
    <div>
      <div
        style={{
          fontFamily: "var(--font-mono)",
          fontSize: size,
          fontWeight: 600,
          lineHeight: 1,
          color,
          fontVariantNumeric: "tabular-nums",
          letterSpacing: "-0.02em",
        }}
      >
        {value}
      </div>
      <div style={{ marginTop: 6 }}>
        <MicroLabel>{label}</MicroLabel>
      </div>
    </div>
  );
}

function DayCell({
  label,
  mm,
  count,
  isToday,
}: {
  label: string;
  mm: string;
  count: number;
  isToday: boolean;
}) {
  const dotColor =
    count === 0
      ? "var(--border-line)"
      : count < 3
      ? "var(--accent-amber-dim)"
      : count < 10
      ? "var(--accent-amber)"
      : "var(--signal-green)";
  return (
    <div
      style={{
        padding: "8px 4px",
        background: isToday ? "var(--bg-2)" : "var(--bg-1)",
        border: `1px solid ${isToday ? "var(--border-strong)" : "var(--border-line)"}`,
        borderRadius: 4,
        textAlign: "center",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 4,
      }}
    >
      <div
        style={{
          fontFamily: "var(--font-mono)",
          fontSize: 10,
          color: isToday ? "var(--fg-1)" : "var(--fg-3)",
          letterSpacing: "0.08em",
          textTransform: "uppercase",
        }}
      >
        {label}
      </div>
      <div
        style={{
          fontFamily: "var(--font-mono)",
          fontSize: 11,
          color: isToday ? "var(--fg-0)" : "var(--fg-2)",
          fontWeight: 600,
          fontVariantNumeric: "tabular-nums",
        }}
      >
        {mm}
      </div>
      <div
        style={{
          width: 6,
          height: 6,
          borderRadius: "50%",
          background: dotColor,
        }}
      />
      <div
        style={{
          fontFamily: "var(--font-mono)",
          fontSize: 10,
          color: count > 0 ? "var(--fg-1)" : "var(--fg-3)",
          fontVariantNumeric: "tabular-nums",
        }}
      >
        {count > 0 ? count : "0"}
      </div>
    </div>
  );
}

const MODULE_COLOR: Record<string, string> = {
  trapline: "var(--signal-cyan)",
  slice: "var(--accent-amber)",
  grammar: "var(--signal-violet)",
  prompt: "#E07AD7",
  rubric: "var(--trap-wrong-scope)",
  science: "var(--signal-green)",
  calc: "#E07AD7",
  // ACT lanes mirror their SAT-subject color as a mnemonic.
  act_reading: "var(--signal-cyan)",
  act_english: "var(--signal-violet)",
  act_math: "var(--accent-amber)",
  act_science: "var(--signal-green)",
};

function ModuleGlyph({ id, color }: { id: string; color: string }) {
  const base = {
    width: 14,
    height: 14,
    viewBox: "0 0 24 24",
    fill: "none",
    stroke: color,
    strokeWidth: 1.5,
    strokeLinecap: "round" as const,
    strokeLinejoin: "round" as const,
    "aria-hidden": true,
  };
  switch (id) {
    case "trapline":
      return (
        <svg {...base}>
          <circle cx="12" cy="12" r="10" />
          <circle cx="12" cy="12" r="6" />
          <circle cx="12" cy="12" r="2" />
        </svg>
      );
    case "slice":
      return (
        <svg {...base}>
          <polygon points="12 2 2 7 12 12 22 7 12 2" />
          <polyline points="2 17 12 22 22 17" />
          <polyline points="2 12 12 17 22 12" />
        </svg>
      );
    case "grammar":
      return (
        <svg {...base}>
          <rect x="3" y="3" width="7" height="7" rx="1" />
          <rect x="14" y="3" width="7" height="7" rx="1" />
          <rect x="3" y="14" width="7" height="7" rx="1" />
          <rect x="14" y="14" width="7" height="7" rx="1" />
        </svg>
      );
    case "prompt":
      return (
        <svg {...base}>
          <circle cx="11" cy="11" r="8" />
          <line x1="21" y1="21" x2="16.65" y2="16.65" />
        </svg>
      );
    case "rubric":
      return (
        <svg {...base}>
          <path d="M3 3v18h18" />
          <path d="M7 14l4-4 4 4 5-5" />
        </svg>
      );
    case "science":
    case "act_science":
      // data + figures: an axis with plotted points
      return (
        <svg {...base}>
          <path d="M3 3v18h18" />
          <circle cx="8" cy="14" r="1.4" />
          <circle cx="13" cy="9" r="1.4" />
          <circle cx="18" cy="12" r="1.4" />
        </svg>
      );
    default:
      // generic subject glyph for the remaining lanes (calc, act_math/english/reading)
      return (
        <svg {...base}>
          <rect x="3" y="3" width="18" height="18" rx="2" />
          <line x1="8" y1="9" x2="16" y2="9" />
          <line x1="8" y1="13" x2="16" y2="13" />
        </svg>
      );
  }
}

/**
 * Below this many answers a percentage is noise, so the tile shows the raw
 * count instead. One correct answer out of one rendered as "100%", which reads
 * as mastery and is the single most misleading thing this screen could say.
 */
const MIN_ATTEMPTS_FOR_PCT = 5;

export function ModuleTile({
  id,
  accuracy,
  attempts = 0,
  correct = 0,
  itemCount,
  pending = false,
}: {
  id: string;
  accuracy: number | null;
  /** Sample size behind `accuracy`. A percentage without it is not a fact. */
  attempts?: number;
  correct?: number;
  itemCount?: number;
  /** Data has not arrived. Render the geometry, assert nothing about the student. */
  pending?: boolean;
}) {
  const color = MODULE_COLOR[id] ?? "var(--fg-2)";
  const enoughForPct = attempts >= MIN_ATTEMPTS_FOR_PCT;
  const pct = accuracy != null && enoughForPct ? Math.round(accuracy * 100) : null;
  // What a screen reader announces. The visual "62%" beside a bar is legible
  // because the bar and the subject name are right there; read aloud it was
  // just "62 percent", naming neither the measure nor the sample.
  const meterLabel =
    pct != null
      ? `${moduleLabel(id)} accuracy ${pct} percent, from ${attempts} question${attempts === 1 ? "" : "s"}`
      : undefined;
  return (
    <Link
      to={`/app/drill/${id}`}
      style={{ display: "block", textDecoration: "none" }}
    >
      <Card tight>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 6,
            marginBottom: 8,
            color,
          }}
        >
          <ModuleGlyph id={id} color={color} />
          <span
            style={{
              fontFamily: "var(--font-mono)",
              fontSize: 11,
              fontWeight: 600,
              letterSpacing: "0.12em",
              textTransform: "uppercase",
              color,
            }}
          >
            {moduleLabel(id)}
          </span>
        </div>
        <div
          style={{
            fontFamily: "var(--font-sans)",
            fontSize: 12,
            color: "var(--fg-2)",
            marginBottom: 8,
            lineHeight: 1.4,
            minHeight: 32,
          }}
        >
          {moduleBlurb(id)}
        </div>
        {pct != null ? (
          <div
            style={{ display: "flex", alignItems: "center", gap: 8 }}
            role="img"
            aria-label={meterLabel}
          >
            <div
              style={{
                height: 2,
                flex: 1,
                background: "var(--bg-3)",
                position: "relative",
                borderRadius: 1,
                overflow: "hidden",
              }}
            >
              <div
                style={{
                  position: "absolute",
                  inset: "0 auto 0 0",
                  width: `${pct}%`,
                  background: color,
                }}
              />
            </div>
            <span
              aria-hidden="true"
              style={{
                fontFamily: "var(--font-mono)",
                fontSize: 11,
                color: "var(--fg-0)",
                fontVariantNumeric: "tabular-nums",
              }}
            >
              {pct}%
            </span>
          </div>
        ) : (
          <span
            style={{
              fontFamily: "var(--font-mono)",
              fontSize: 10,
              color: "var(--fg-3)",
              letterSpacing: "0.06em",
              textTransform: "uppercase",
            }}
          >
            {pending
              ? "\u00a0"
              : attempts > 0
                // Too few to call a percentage, so state the actual tally.
                ? `${correct} of ${attempts} correct`
                : itemCount != null
                  ? `${itemCount} items`
                  : "Not started"}
          </span>
        )}
      </Card>
    </Link>
  );
}
